import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { useImportantNotes } from '../contexts/ImportantNotesContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Persistent red banner displayed at the top of a Prospect detail / Devis
 * detail / Facture detail page when the prospect carries at least one
 * important note. Reads the same `prospects-important-flags` cache as the
 * rest of the app, then lazily fetches the full text when shown.
 */
export default function ImportantNotesBanner({ prospectId, onOpen }) {
    const { flags } = useImportantNotes();
    const count = flags[prospectId] || 0;
    const [notes, setNotes] = useState([]);

    const fetch = useCallback(async () => {
        if (!prospectId) return;
        try {
            const res = await axios.get(
                `${API_URL}/api/prospects/${prospectId}/notes-importantes`,
                { withCredentials: true }
            );
            setNotes(res.data || []);
        } catch {
            setNotes([]);
        }
    }, [prospectId]);

    useEffect(() => {
        if (count > 0) fetch();
        else setNotes([]);
    }, [count, fetch]);

    if (count === 0 || notes.length === 0) return null;

    return (
        <div
            className="mb-4 rounded-lg border-l-4 border-red-500 bg-red-50 p-4 shadow-sm"
            data-testid="important-notes-banner"
        >
            <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="font-bold text-red-700 text-sm uppercase tracking-wide">
                            {count > 1 ? `${count} notes importantes` : 'Note importante'}
                        </h3>
                        {onOpen && (
                            <button
                                onClick={onOpen}
                                className="text-xs font-medium text-red-700 hover:text-red-900 underline"
                                data-testid="important-notes-banner-manage"
                            >
                                Gérer
                            </button>
                        )}
                    </div>
                    <ul className="space-y-1.5">
                        {notes.slice(0, 3).map((n) => (
                            <li key={n.id} className="text-sm text-gray-900">
                                <span className="whitespace-pre-wrap">{n.content}</span>
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    — {n.created_by_name}
                                    {n.created_at
                                        ? `, ${format(new Date(n.created_at), 'dd/MM/yyyy', { locale: fr })}`
                                        : ''}
                                </span>
                            </li>
                        ))}
                        {notes.length > 3 && (
                            <li className="text-xs italic text-red-700">
                                + {notes.length - 3} note(s) supplémentaire(s)…
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}
